import React, { useMemo } from 'react';
import { PAGE_SIZE_OPTIONS } from '@/constants/pagination';
import {
  Box,
  Button,
  Chip,
  FormControlLabel,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import { useWriteGuard } from '@/hooks/useWriteGuard';
import SearchIcon from '@mui/icons-material/Search';
import DeleteIcon from '@mui/icons-material/Delete';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { DataGrid, type GridColDef, type GridRowSelectionModel } from '@mui/x-data-grid';
import { clampQuantity, formatDateTime, hasSelection } from '@/components/bulk-dispose/utils';
import type { BulkDisposeConditionCode, PendingDisposeRow } from '@/components/bulk-dispose/types';

const CONDITION_CODE_OPTIONS: BulkDisposeConditionCode[] = ['H', 'A', 'B'];
const DISPOSABLE_STATUSES = new Set(['available', 'assigned', 'binned', 'maintenance']);

type PendingDisposeTabProps = {
  rows: PendingDisposeRow[];
  isFetching: boolean;
  assetNameFilter: string;
  linNsnFilter: string;
  fromDate: string;
  toDate: string;
  selectedRowIds: GridRowSelectionModel;
  qtyByRow: Record<string, number>;
  conditionUpdatePendingByRow: Record<string, boolean>;
  pendingChangesOnly: boolean;
  selectedQty: number;
  selectedPendingTypeChangeQty: number;
  totalQty: number;
  isSubmitting: boolean;
  isUpdatingPendingTypeChange: boolean;
  onAssetNameFilterChange: (value: string) => void;
  onLinNsnFilterChange: (value: string) => void;
  onFromDateChange: (value: string) => void;
  onToDateChange: (value: string) => void;
  onPendingChangesOnlyChange: (value: boolean) => void;
  onClearFilters: () => void;
  onQtyChange: (row: PendingDisposeRow, rawValue: string) => void;
  onConditionCodeChange: (row: PendingDisposeRow, conditionCode: BulkDisposeConditionCode) => void;
  onOpenAdjustType: (row: PendingDisposeRow) => void;
  onSelectAllVisible: () => void;
  onDeselectAllVisible: () => void;
  onSelectionChange: (selection: GridRowSelectionModel) => void;
  onOpenConfirm1348: () => void;
  onOpenConfirm4949: () => void;
  onReturnToInventory: () => void;
  onRemoveManualEntry?: (id: string) => void;
};

export const PendingDisposeTab: React.FC<PendingDisposeTabProps> = ({
  rows,
  isFetching,
  assetNameFilter,
  linNsnFilter,
  fromDate,
  toDate,
  selectedRowIds,
  qtyByRow,
  conditionUpdatePendingByRow,
  pendingChangesOnly,
  selectedQty,
  selectedPendingTypeChangeQty,
  totalQty,
  isSubmitting,
  isUpdatingPendingTypeChange,
  onAssetNameFilterChange,
  onLinNsnFilterChange,
  onFromDateChange,
  onToDateChange,
  onPendingChangesOnlyChange,
  onClearFilters,
  onQtyChange,
  onConditionCodeChange,
  onOpenAdjustType,
  onSelectAllVisible,
  onDeselectAllVisible,
  onSelectionChange,
  onOpenConfirm1348,
  onOpenConfirm4949,
  onReturnToInventory,
  onRemoveManualEntry,
}) => {
  const isViewer = useWriteGuard();
  const dateRangeError =
    fromDate !== '' && toDate !== '' && toDate < fromDate
      ? 'End date must be after start date'
      : undefined;
  const columns = useMemo<GridColDef<PendingDisposeRow>[]>(
    () => [
      {
        field: 'lin',
        headerName: 'LIN',
        width: 140,
      },
      {
        field: 'nsn',
        headerName: 'NSN',
        width: 190,
      },
      {
        field: 'assetName',
        headerName: 'Nomenclature',
        flex: 1,
        minWidth: 220,
      },
      {
        field: 'size',
        headerName: 'Size',
        width: 120,
      },
      {
        field: 'assetStatusKey',
        headerName: 'Status',
        width: 150,
        renderCell: ({ row }) => {
          if (row.id.startsWith('manual:')) return null;
          const status = row.assetStatusKey;
          if (!status) return null;
          const isDisposable = DISPOSABLE_STATUSES.has(status);
          if (isDisposable) return null;
          return (
            <Chip
              icon={<WarningAmberIcon fontSize="small" />}
              label={status}
              color="error"
              size="small"
              variant="outlined"
            />
          );
        },
      },
      {
        field: 'qty',
        headerName: 'Qty',
        width: 140,
        sortable: false,
        filterable: false,
        renderCell: ({ row }) => {
          const selected = hasSelection(selectedRowIds, row.id);
          const fallback = selected ? row.availableCount : 0;
          const value = clampQuantity(qtyByRow[row.id] ?? fallback, row.availableCount);

          return (
            <TextField
              size="small"
              type="number"
              value={value}
              inputProps={{ min: 0, max: row.availableCount, style: { textAlign: 'right' } }}
              disabled={isViewer}
              onChange={(event) => onQtyChange(row, event.target.value)}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">/ {row.availableCount}</InputAdornment>
                ),
              }}
              sx={{ width: 120 }}
            />
          );
        },
      },
      {
        field: 'dateMarked',
        headerName: 'Date Marked',
        width: 210,
        valueFormatter: (value) => formatDateTime(value),
      },
      {
        field: 'serviceType',
        headerName: 'Service Type',
        width: 260,
        sortable: false,
        filterable: false,
        renderCell: ({ row }) => {
          const hasBothTypes = row.serviceableCount > 0 && row.nonServiceableCount > 0;
          if (hasBothTypes) {
            return (
              <Stack direction="row" spacing={0.5} alignItems="center">
                <Chip
                  label={`Serviceable (${row.serviceableCount})`}
                  color="success"
                  size="small"
                  variant="outlined"
                />
                <Chip
                  label={`Non-Serviceable (${row.nonServiceableCount})`}
                  color="warning"
                  size="small"
                />
              </Stack>
            );
          }

          const normalized = row.serviceType.toLowerCase();
          const isServiceable = normalized === 'serviceable';
          const isNonServiceable = normalized.includes('non');

          return (
            <Chip
              label={row.serviceType}
              color={isServiceable ? 'success' : isNonServiceable ? 'warning' : 'default'}
              size="small"
              variant={isServiceable ? 'outlined' : 'filled'}
            />
          );
        },
      },
      {
        field: 'pendingTypeChange',
        headerName: 'Pending Type Change',
        width: 290,
        sortable: false,
        filterable: false,
        renderCell: ({ row }) => {
          if (row.pendingTypeChangeQuantity <= 0) {
            return (
              <Typography variant="caption" color="text.secondary">
                None
              </Typography>
            );
          }

          if (row.pendingTypeChangeMixed) {
            return (
              <Chip
                size="small"
                color="warning"
                variant="outlined"
                label={`${row.pendingTypeChangeQuantity} item(s) pending • Mixed targets`}
              />
            );
          }

          const targetName = row.pendingTypeChangeTargetName ?? 'Selected catalog';
          return (
            <Stack spacing={0.3}>
              <Chip
                size="small"
                color="info"
                variant="outlined"
                label={`${row.pendingTypeChangeQuantity} item(s) pending`}
              />
              <Typography variant="caption" color="text.secondary" noWrap>
                {targetName}
              </Typography>
            </Stack>
          );
        },
      },
      {
        field: 'conditionCode',
        headerName: 'Condition Code',
        width: 180,
        sortable: false,
        filterable: false,
        renderCell: ({ row }) => {
          const value = row.conditionCode ?? '';
          const isUpdating = Boolean(conditionUpdatePendingByRow[row.id]);
          const hasMixedServiceability = row.serviceableCount > 0 && row.nonServiceableCount > 0;
          const shouldShowMixedPlaceholder =
            (row.conditionCodeMixed || hasMixedServiceability) && !value;
          const placeholderLabel = shouldShowMixedPlaceholder ? 'Mixed' : 'Select';

          return (
            <TextField
              select
              size="small"
              value={value}
              disabled={isViewer || isUpdating}
              onChange={(event) => {
                const nextCode = event.target.value as BulkDisposeConditionCode;
                if (!CONDITION_CODE_OPTIONS.includes(nextCode)) {
                  return;
                }
                onConditionCodeChange(row, nextCode);
              }}
              SelectProps={{
                displayEmpty: true,
                renderValue: (selected: unknown) => {
                  if (!selected) {
                    return placeholderLabel;
                  }
                  return selected as string;
                },
              }}
              sx={{ width: 150 }}
            >
              <MenuItem value="" disabled>
                {placeholderLabel}
              </MenuItem>
              {CONDITION_CODE_OPTIONS.map((conditionCode) => (
                <MenuItem key={conditionCode} value={conditionCode}>
                  {conditionCode}
                </MenuItem>
              ))}
            </TextField>
          );
        },
      },
      {
        field: 'recoverable',
        headerName: 'Recoverable Type',
        width: 210,
        renderCell: ({ row }) => {
          const normalized = row.recoverable.toUpperCase();
          const color = normalized.includes('NON')
            ? 'warning'
            : normalized.includes('RECOVERABLE')
              ? 'success'
              : 'default';
          return <Chip label={row.recoverable} color={color} size="small" variant="outlined" />;
        },
      },
      {
        field: 'adjustType',
        headerName: 'Actions',
        width: 150,
        sortable: false,
        filterable: false,
        renderCell: ({ row }) => {
          if (row.id.startsWith('manual:')) {
            return (
              <IconButton
                size="small"
                color="error"
                disabled={isSubmitting}
                aria-label="Remove entry"
                onClick={() => onRemoveManualEntry?.(row.id.replace('manual:', ''))}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            );
          }
          return (
            <Button
              variant="text"
              size="small"
              disabled={isViewer || isSubmitting || isUpdatingPendingTypeChange}
              onClick={() => onOpenAdjustType(row)}
            >
              {row.pendingTypeChangeQuantity > 0 ? 'Edit Type' : 'Adjust Type'}
            </Button>
          );
        },
      },
    ],
    [
      conditionUpdatePendingByRow,
      isSubmitting,
      isUpdatingPendingTypeChange,
      isViewer,
      onConditionCodeChange,
      onOpenAdjustType,
      onQtyChange,
      onRemoveManualEntry,
      qtyByRow,
      selectedRowIds,
    ],
  );

  return (
    <>
      <Paper elevation={1} sx={{ p: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid size={{ xs: 12, md: 4 }}>
            <TextField
              fullWidth
              size="small"
              label="Asset Name"
              value={assetNameFilter}
              onChange={(event) => onAssetNameFilterChange(event.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 3 }}>
            <TextField
              fullWidth
              size="small"
              label="LIN / NSN"
              value={linNsnFilter}
              onChange={(event) => onLinNsnFilterChange(event.target.value)}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 2 }}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label="From"
              value={fromDate}
              onChange={(event) => onFromDateChange(event.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 2 }}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label="To"
              value={toDate}
              onChange={(event) => onToDateChange(event.target.value)}
              InputLabelProps={{ shrink: true }}
              error={dateRangeError !== undefined}
              helperText={dateRangeError}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 1 }}>
            <Button fullWidth variant="text" onClick={onClearFilters}>
              Clear
            </Button>
          </Grid>
          <Grid size={{ xs: 12, md: 12 }}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems="center">
              <FormControlLabel
                control={
                  <Switch
                    checked={pendingChangesOnly}
                    onChange={(event) => onPendingChangesOnlyChange(event.target.checked)}
                  />
                }
                label="Pending changes only"
              />
              <Box sx={{ flex: 1 }} />
              {!isViewer && (
                <>
                  <Button size="small" onClick={onSelectAllVisible}>
                    Select All (Visible)
                  </Button>
                  <Button size="small" onClick={onDeselectAllVisible}>
                    Deselect All
                  </Button>
                </>
              )}
            </Stack>
          </Grid>
        </Grid>
      </Paper>

      <Paper elevation={1} sx={{ p: 2 }}>
        <Box sx={{ width: '100%' }}>
          <DataGrid
            rows={rows}
            columns={columns}
            loading={isFetching}
            autoHeight
            checkboxSelection={!isViewer}
            disableRowSelectionOnClick
            rowSelectionModel={selectedRowIds}
            onRowSelectionModelChange={onSelectionChange}
            pageSizeOptions={PAGE_SIZE_OPTIONS}
            initialState={{
              pagination: { paginationModel: { pageSize: 25 } },
            }}
            getRowClassName={(params) => {
              if (params.row.id.startsWith('manual:')) return 'manual-entry-row';
              return params.row.pendingTypeChangeQuantity > 0 ? 'pending-type-change-row' : '';
            }}
            sx={{
              '& .MuiDataGrid-columnHeaders': {
                bgcolor: 'background.default',
              },
              '& .pending-type-change-row': {
                bgcolor: 'rgba(2, 136, 209, 0.08)',
              },
              '& .manual-entry-row': {
                bgcolor: 'rgba(237, 108, 2, 0.08)',
              },
            }}
          />
        </Box>

        <Stack spacing={1} alignItems="flex-end" sx={{ pt: 2 }}>
          <Typography variant="body2" color="text.secondary" textAlign="right">
            {selectedQty} of {totalQty} items selected
          </Typography>
          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              color="primary"
              onClick={onReturnToInventory}
              disabled={isSubmitting || isViewer || selectedQty <= 0}
            >
              Return to Inventory
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={onOpenConfirm4949}
              disabled={isSubmitting || isViewer || selectedPendingTypeChangeQty <= 0}
            >
              Generate 4949 ({selectedPendingTypeChangeQty} item
              {selectedPendingTypeChangeQty === 1 ? '' : 's'})
            </Button>
            <Button
              variant="contained"
              color="warning"
              onClick={onOpenConfirm1348}
              disabled={isSubmitting || isViewer || selectedQty <= 0}
            >
              Generate 1348
            </Button>
          </Stack>
        </Stack>
      </Paper>
    </>
  );
};
